import { Alert, Spin } from 'antd';
import flvjs from 'flv.js';
import React, { useEffect, useRef } from 'react';
import './index.less';

interface FlvPlayerProps {
  url: string;
  width?: string | number;
  height?: string | number;
  autoPlay?: boolean;
  controls?: boolean;
  muted?: boolean;
  className?: string;
}

const FlvPlayer: React.FC<FlvPlayerProps> = ({
  url,
  width = '100%',
  height = 'auto',
  autoPlay = true,
  muted = true,
  controls = true,
  className = '',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<flvjs.Player | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (!flvjs.isSupported()) {
      setError('当前浏览器不支持 FLV 播放');
      setIsLoading(false);
      return;
    }

    if (videoRef.current && url) {
      try {
        // 创建 FLV 播放器
        playerRef.current = flvjs.createPlayer(
          {
            type: 'flv',
            url: url,
            isLive: true,
            hasAudio: false, // 流没有音频通道
            hasVideo: true,
          },
          {
            enableWorker: false,
            enableStashBuffer: false,
            stashInitialSize: 128,
            lazyLoad: false,
            lazyLoadMaxDuration: 3 * 60,
            lazyLoadRecoverDuration: 30,
            deferLoadAfterSourceOpen: false,
            autoCleanupSourceBuffer: true,
            autoCleanupMaxBackwardDuration: 3,
            autoCleanupMinBackwardDuration: 2,
            fixAudioTimestampGap: false,
          },
        );

        playerRef.current.attachMediaElement(videoRef.current);
        playerRef.current.load();

        // 监听事件
        playerRef.current.on(flvjs.Events.LOADING_COMPLETE, () => {
          setIsLoading(false);
        });

        playerRef.current.on(flvjs.Events.ERROR, (errType, errDetail) => {
          console.error('[FLV Player] Error:', errType, errDetail);
          setError(`播放错误: ${errType} - ${errDetail}`);
          setIsLoading(false);
        });

        playerRef.current.on(flvjs.Events.METADATA_ARRIVED, () => {
          console.log('[FLV Player] Metadata arrived');
          setIsLoading(false);
          // 元数据到达后，尝试播放
          if (autoPlay && videoRef.current) {
            videoRef.current.play().catch((err: Error) => {
              console.warn('[FLV Player] 自动播放失败:', err);
            });
          }
        });

        // 监听video元素事件，确保播放状态正确
        const videoElement = videoRef.current;
        const handleCanPlay = () => {
          console.log('[FLV Player] Can play');
          setIsLoading(false);
        };

        const handlePlaying = () => {
          console.log('[FLV Player] Playing');
          setIsLoading(false);
        };

        const handleWaiting = () => {
          console.log('[FLV Player] Waiting');
        };

        videoElement.addEventListener('canplay', handleCanPlay);
        videoElement.addEventListener('playing', handlePlaying);
        videoElement.addEventListener('waiting', handleWaiting);
      } catch (err) {
        console.error('[FLV Player] Init Error:', err);
        setError('播放器初始化失败');
        setIsLoading(false);
      }
    }

    // 清理函数
    return () => {
      if (videoRef.current) {
        const videoElement = videoRef.current;
        videoElement.removeEventListener('canplay', () => {});
        videoElement.removeEventListener('playing', () => {});
        videoElement.removeEventListener('waiting', () => {});
      }

      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.unload();
          playerRef.current.detachMediaElement();
          playerRef.current.destroy();
          playerRef.current = null;
        } catch (err) {
          console.error('[FLV Player] Destroy Error:', err);
        }
      }
    };
  }, [url, autoPlay]);

  return (
    <div
      className={`flv-player-container ${className}`}
      style={{ width, height }}
    >
      {isLoading && (
        <div className="flv-player-loading">
          <Spin tip="加载视频流中..." />
        </div>
      )}
      {error && (
        <Alert
          message="播放错误"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <video
        ref={videoRef}
        style={{
          width,
          height,
          display: error ? 'none' : 'block',
        }}
        controls={controls}
        muted={muted}
        playsInline
      >
        <track kind="captions" />
      </video>
    </div>
  );
};

export default FlvPlayer;
